import * as React from 'react';
import {
  Download,
  Edit3,
  FileSpreadsheet,
  FileText,
  Info,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  toast,
  ConfirmDialog,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { downloadBlob } from '../../../../lib/downloadBlob';
import { dashboardT } from '../../../../lib/messages';
import { ShigoPermission } from '../../../auth/permissions';
import { useShigoPermissions } from '../../../auth/useShigoPermissions';
import { BlockedCreativeDialog } from '../components/BlockedCreativeDialog/BlockedCreativeDialog';
import { BlockedListUploadDialog } from '../components/BlockedListUploadDialog/BlockedListUploadDialog';
import { useBlockedCreativesQuery, useBlockedListMutations } from '../hooks';
import {
  blockedListPageSizes,
  blockedReasonLabel,
  BlockedListFileFormat,
  BlockedListSortDirection,
  BlockedListSortProperty,
  isBlockedListSortProperty,
  type BlockedCreative,
} from '../model';
import { useBlockedListRouteFilters } from '../useBlockedListRouteFilters';
import { formatDisplayDate } from '../../../../lib/format';

export function BlockedCreativesPage() {
  const { can } = useShigoPermissions();
  const canCreate = can(ShigoPermission.BlocklistCreativesWrite);
  const canEdit = can(ShigoPermission.BlocklistCreativesEdit);
  const canDelete = can(ShigoPermission.BlocklistCreativesDelete);
  const routeFilters = useBlockedListRouteFilters();
  const { filter } = routeFilters;
  const search = useDebouncedValue(
    filter.searchTerm,
    (value) => {
      routeFilters.setSearch(value);
    },
    650,
  );
  const query = useBlockedCreativesQuery(filter);
  const mutations = useBlockedListMutations();
  const [editor, setEditor] = React.useState<BlockedCreative | null | undefined>();
  const [uploadFormat, setUploadFormat] = React.useState<BlockedListFileFormat | null>(null);
  const [deleting, setDeleting] = React.useState<BlockedCreative | null>(null);

  async function exportFile(format: BlockedListFileFormat) {
    try {
      const blob = await mutations.download.mutateAsync({
        format,
        searchTerm: filter.searchTerm,
      });
      if (!blob.size) {
        toast.error(dashboardT('blockedList.exportEmpty'));
        return;
      }
      downloadBlob(
        blob,
        `blocked-creatives.${format === BlockedListFileFormat.Csv ? 'csv' : 'xlsx'}`,
      );
    } catch (error) {
      showRequestError(error, dashboardT('blockedList.exportError'));
    }
  }

  async function deleteCreative() {
    if (!deleting) return;
    try {
      await mutations.delete.mutateAsync(deleting.id);
      toast.success(dashboardT('blockedList.deleted'));
      setDeleting(null);
    } catch (error) {
      showRequestError(error, dashboardT('blockedList.deleteError'));
    }
  }

  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<BlockedCreative>([
        {
          id: BlockedListSortProperty.Creative,
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('blockedList.creative'),
          headerClassName: 'w-60 pl-4',
          className: 'w-60 pl-4 font-medium',
          sortable: true,
          value: (item) =>
            canEdit ? (
              <button
                type="button"
                className={dashboardTableTextActionClassName()}
                onClick={() => {
                  setEditor(item);
                }}
              >
                {item.creative}
              </button>
            ) : (
              item.creative
            ),
        },
        {
          id: 'blockedFor',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('blockedList.blockedFor'),
          headerClassName: 'w-64',
          className: 'w-64',
          value: (item) =>
            item.blockAll
              ? dashboardT('blockedList.allPublishers')
              : renderTruncatedTooltip(
                  dashboardT('blockedList.publishers'),
                  item.publishers.map((publisher) => publisher.name).join(', '),
                ),
        },
        {
          id: 'domains',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('blockedList.domains'),
          headerClassName: 'w-20 text-center',
          className: 'w-20 text-center',
          value: (item) =>
            item.domains.length
              ? renderInfoTooltip(
                  dashboardT('blockedList.viewDomains'),
                  item.domains.map((domain) => domain.url).join(', '),
                )
              : null,
        },
        {
          id: 'reason',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('blockedList.reason'),
          headerClassName: 'w-32',
          className: 'w-32',
          value: (item) => dashboardT(blockedReasonLabel(item.reason)),
        },
        {
          id: 'comment',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('blockedList.comment'),
          headerClassName: 'w-20 text-center',
          className: 'w-20 text-center',
          value: (item) =>
            item.comment
              ? renderInfoTooltip(dashboardT('blockedList.viewComment'), item.comment)
              : null,
        },
        {
          id: BlockedListSortProperty.CreatedOn,
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('blockedList.created'),
          headerClassName: 'w-32',
          className: 'w-32 whitespace-nowrap',
          sortable: true,
          value: (item) => formatDisplayDate(item.createdOn),
        },
        {
          id: BlockedListSortProperty.UpdatedOn,
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('blockedList.updated'),
          headerClassName: 'w-32',
          className: 'w-32 whitespace-nowrap',
          sortable: true,
          value: (item) => formatDisplayDate(item.updatedOn),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canEdit && !canDelete,
          cell: (item) => (
            <DashboardTableRowActions label={dashboardT('blockedList.actions')}>
              {canEdit ? (
                <DashboardTableRowActionItem
                  onSelect={() => {
                    setEditor(item);
                  }}
                >
                  <Edit3 className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('blockedList.edit')}
                </DashboardTableRowActionItem>
              ) : null}
              {canDelete ? (
                <DashboardTableRowActionItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => {
                    setDeleting(item);
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('blockedList.delete')}
                </DashboardTableRowActionItem>
              ) : null}
            </DashboardTableRowActions>
          ),
        },
      ]),
    [canDelete, canEdit],
  );

  return (
    <>
      <DashboardDataTable
        title={dashboardT('blockedList.title')}
        tableLabel={dashboardT('blockedList.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(item) => item.id}
        loading={query.isLoading}
        loadingMessage={dashboardT('blockedList.loading')}
        errorMessage={query.isError ? dashboardT('blockedList.loadError') : undefined}
        emptyMessage={dashboardT('blockedList.empty')}
        minWidthClassName="min-w-[1120px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
              placeholder={dashboardT('blockedList.search')}
              aria-label={dashboardT('blockedList.search')}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    disabled={query.isFetching}
                    aria-label={dashboardT('blockedList.refresh')}
                    onClick={() => void query.refetch()}
                  >
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{dashboardT('blockedList.refresh')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {canCreate ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Upload className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('blockedList.bulkUpload')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {renderFileFormatItems('upload', setUploadFormat)}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={mutations.download.isPending}>
                  <Download className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('blockedList.bulkExport')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {renderFileFormatItems('export', (format) => void exportFile(format))}
              </DropdownMenuContent>
            </DropdownMenu>
            {canCreate ? (
              <Button
                type="button"
                onClick={() => {
                  setEditor(null);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('blockedList.add')}
              </Button>
            ) : null}
          </>
        }
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === BlockedListSortDirection.Ascending
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (value) => {
            if (!value) {
              routeFilters.setSort(null, filter.sortDirection);
              return;
            }
            if (!isBlockedListSortProperty(value.key)) return;
            routeFilters.setSort(
              value.key,
              value.direction === DashboardDataTableSortDirection.Asc
                ? BlockedListSortDirection.Ascending
                : BlockedListSortDirection.Descending,
            );
          },
        }}
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: blockedListPageSizes,
          disabled: query.isFetching,
          onChange: (value) => {
            routeFilters.setPagination(value.page, value.pageSize);
          },
        }}
      />

      {editor !== undefined ? (
        <BlockedCreativeDialog
          creative={editor}
          onClose={() => {
            setEditor(undefined);
          }}
        />
      ) : null}
      {uploadFormat ? (
        <BlockedListUploadDialog
          format={uploadFormat}
          onClose={() => {
            setUploadFormat(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={dashboardT('blockedList.deleteTitle')}
        description={dashboardT('blockedList.deleteDescription', {
          creative: deleting?.creative ?? '',
        })}
        cancelLabel={dashboardT('blockedList.cancel')}
        confirmLabel={dashboardT('blockedList.delete')}
        pending={mutations.delete.isPending}
        destructive
        onConfirm={() => {
          void deleteCreative();
        }}
      />
    </>
  );
}

function renderInfoTooltip(label: string, value: string) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" aria-label={label}>
          <Info className="h-4 w-4" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-80 whitespace-normal">{value}</TooltipContent>
    </Tooltip>
  );
}

function renderTruncatedTooltip(label: string, value: string) {
  if (!value) return null;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${value}`}
          className="block w-full truncate text-left focus:outline-none focus-visible:ring-2"
        >
          {value}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-80 whitespace-normal">{value}</TooltipContent>
    </Tooltip>
  );
}

function renderFileFormatItems(
  mode: 'upload' | 'export',
  onSelect: (format: BlockedListFileFormat) => void,
) {
  return (
    <>
      <DropdownMenuItem
        onSelect={() => {
          onSelect(BlockedListFileFormat.Csv);
        }}
      >
        <FileText className="h-4 w-4" aria-hidden="true" />
        {dashboardT(mode === 'upload' ? 'blockedList.uploadCsv' : 'blockedList.exportCsv')}
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={() => {
          onSelect(BlockedListFileFormat.Excel);
        }}
      >
        <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
        {dashboardT(mode === 'upload' ? 'blockedList.uploadExcel' : 'blockedList.exportExcel')}
      </DropdownMenuItem>
    </>
  );
}
